import { useEffect, useState } from 'react'
import {
  Box,
  chakra,
  Container,
  Flex,
  HStack,
  Image,
  Link as ChakraLink,
  Spinner,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react'
import Link from 'next/link'
import { formatUnits } from 'viem'

import Card from '~/components/common/Card'
import HelpTooltip from '~/components/dashboard/HelpTooltip'
import StakeModal from '~/components/dashboard/StakeModal'
import { ButtonSize, CustomButton } from '~/components/layout/CustomButton'
import NetworkWarningBanner from '~/components/layout/NetworkBanner'
import { SmallButton } from '~/components/layout/SmallButton'
import { DEFAULT_DECIMALS } from '~/constants'
import { formatPercentageString, formatPriceNumberWithSymbol, formatTokenAmount } from '~/helpers'
import { useClaimModal } from '~/hooks/useClaimModal'
import useDeeprStaking from '~/hooks/useDeeprStaking'
import { useMarkets } from '~/hooks/useMarkets'
import useTimeRemainingMessage from '~/hooks/useTimeRemainingMessage'
import { useTranslation } from '~/lib/i18n'
import { theme } from '~/styles/theme'

const Staking = () => {
  const { t } = useTranslation('staking')
  const { t: tDashboard } = useTranslation('dashboard')

  const [isLoadingPage, setIsLoadingPage] = useState(true)
  const [hasLoadedImage, setHasLoadedImage] = useState(false)

  const isMobileViewport = useBreakpointValue({ base: true, md: false })

  useEffect(() => {
    setTimeout(() => {
      setIsLoadingPage(false)
    }, 1000)
  }, [])

  const { isProtocolDisabled } = useMarkets()

  const { distributionToken } = useClaimModal()

  const { balance = 0, distributionTokenPrice, underlyingDecimals } = distributionToken

  const deeprStaking = useDeeprStaking()

  const {
    userStakingInfo,
    harvest,
    withdraw,
    marketStakingInfo,
    onOpenStakingModal,
    onCloseStakingModal,
    isStakingModalOpen,
    isLoadingHarvest,
    isLoadingWithdraw,
  } = deeprStaking

  const { hasActiveWithdrawRequest } = userStakingInfo

  const lockedRequestedAmount = parseFloat(
    formatUnits(userStakingInfo?.lockedRequestedAmount ?? 0, underlyingDecimals ?? DEFAULT_DECIMALS)
  )

  const stakedAmount = parseFloat(
    formatUnits(userStakingInfo?.stakedAmount ?? 0, underlyingDecimals ?? DEFAULT_DECIMALS)
  )
  const hasAlreadyStaked = stakedAmount > 0

  const pendingRewards = parseFloat(
    formatUnits((userStakingInfo.pendingRewards as bigint) ?? BigInt(0), underlyingDecimals ?? DEFAULT_DECIMALS)
  )

  const handleMainButtonClick = () => {
    if (hasActiveWithdrawRequest && canWithdrawFunds) {
      return withdraw()
    }

    if (hasAlreadyStaked) {
      return harvest()
    }

    return onOpenStakingModal()
  }

  const handleManageClick = () => {
    onOpenStakingModal()
  }

  const userLockEndTimestamp = Number(userStakingInfo.userLockEndTimestamp) * 1000

  const timeRemainingMessage = `${t('coolingPeriodEndsIn')} ${useTimeRemainingMessage(userLockEndTimestamp)}`

  const canWithdrawFunds = Date.now() > userLockEndTimestamp

  const getCTAButtonText = () => {
    if (hasActiveWithdrawRequest) {
      return t('withdraw')
    }

    if (hasAlreadyStaked) {
      return t('claim')
    }

    return t('stake')
  }

  const getStakeValueToDisplay = () => {
    if (hasActiveWithdrawRequest) {
      return lockedRequestedAmount
    }

    if (hasAlreadyStaked) {
      return stakedAmount
    }

    return balance
  }

  const stakeValueToDisplay = getStakeValueToDisplay()

  const valuesColor = hasActiveWithdrawRequest ? 'text.secondary' : 'text.primary'

  const stakeSections = formatTokenAmount(stakeValueToDisplay, '', 2).toString().split('.')
  const stakeInt = stakeSections[0].trim()
  const stakeDecimals = stakeSections[1] ?? '00'

  const rewards = hasActiveWithdrawRequest
    ? formatTokenAmount(0)
    : formatTokenAmount(
        hasAlreadyStaked ? pendingRewards : marketStakingInfo.potentialDailyRewards,
        '',
        2,
        'standard',
        {
          displayMinimumTokenAmount: false,
        }
      )

  const rewardsSections = rewards.toString().split('.')
  const rewardsInt = rewardsSections[0].trim()
  const rewardsDecimals = rewardsSections[1] ?? '00'

  if (isLoadingPage) {
    return (
      <Container maxW={{ base: 'container.md', lg: 'container.xl' }}>
        <Flex alignItems="center" justifyContent="center" h="70vh">
          <Spinner />
        </Flex>
      </Container>
    )
  }

  return (
    <>
      {isProtocolDisabled && (
        <NetworkWarningBanner
          label={tDashboard('networkDown.banner')}
          buttonLabel={tDashboard('networkDown.bannerButton')}
        />
      )}

      <Container maxW={{ base: 'container.md', lg: 'container.xl' }} justifyContent="center">
        <Card>
          <Flex
            background={theme.colors.background.box}
            border="1px solid #FFFFFF12"
            bgSize={{ base: 'cover', md: 'auto' }}
            borderRadius="2xl"
            w={{ base: 'full', lg: 'initial' }}
            h={{ base: 'auto', md: '495px' }}
            alignItems={{ base: 'center', lg: 'flex-start' }}
            justifyContent="center"
            gap={{ base: '16px', lg: '60px' }}
            display="flex"
            flexDir={{ base: 'column', md: 'row' }}
            px={{ base: '16px', lg: '60px' }}
            py={{ base: '16px', md: '60px' }}
            mb={{ base: 6, md: 0 }}
          >
            <Flex
              w={{ md: 'full' }}
              px={{ md: 6, lg: 0 }}
              alignItems="center"
              justifyContent="space-between"
              flexDir="column"
              h="100%"
              gap={4}
            >
              <Box pos="relative">
                <Image
                  w={{ base: '180px', md: '260px' }}
                  h={{ base: '186px', md: '270px' }}
                  src="/assets/staking-cover.svg"
                  onLoad={() => setHasLoadedImage(true)}
                />
                {hasLoadedImage && (
                  <chakra.video
                    playsInline
                    muted
                    autoPlay
                    loop
                    position="absolute"
                    left={0}
                    top={0}
                    blendMode="screen"
                    visibility={{ base: 'hidden', md: 'visible' }}
                  >
                    <source src="/videos/staking_animation_layer.mp4" type="video/mp4" />
                  </chakra.video>
                )}
              </Box>
              <VStack>
                <Text
                  color="text.primary"
                  fontWeight="bold"
                  fontSize={{ base: 'md', md: '2xl' }}
                  lineHeight={{ base: '24px', md: '32px' }}
                  w={{ base: '184px', md: 'auto' }}
                  textAlign="center"
                >
                  {t('title')}
                </Text>
                <ChakraLink
                  as={Link}
                  href="https://medium.com/@Deepr.Finance/unlock-passive-earnings-staking-deepr-with-deepr-finance-a3d932473afd"
                  target="_blank"
                  fontWeight="bold"
                  fontSize="16px"
                  lineHeight="24px"
                  color="primary"
                >
                  {t(isMobileViewport ? 'learnMoreLinkMobile' : 'learnMoreLink')}
                </ChakraLink>
              </VStack>
            </Flex>
            <Flex
              alignItems="center"
              justifyContent="center"
              flexDir="column"
              gap={2}
              minW={{ base: '100%', md: '340px' }}
              mt={{ base: 6, md: 0 }}
            >
              <Card
                w={{ base: '100%' }}
                justifyContent="center"
                h="83px"
                alignItems={{ base: 'flex-start' }}
                background={theme.colors.background.box}
                borderRadius="10px"
                p={4}
                dropShadow="box-shadow: 0px 1px 0px 0px rgba(255, 255, 255, 0.04) inset;"
              >
                <Flex direction="column">
                  <Text fontWeight="medium" fontSize="14px" lineHeight="16px" color="text.secondary" mb={1}>
                    {t('stakingAPR')}
                  </Text>
                  <Text fontSize="24px" lineHeight="32px" fontWeight="700" color={valuesColor}>
                    {formatPercentageString(marketStakingInfo.APR)}
                  </Text>
                </Flex>
              </Card>
              <Card
                justifyContent="center"
                w={{ base: '100%' }}
                h="102px"
                alignItems={{ base: 'flex-start' }}
                background={theme.colors.background.box}
                dropShadow="box-shadow: 0px 1px 0px 0px rgba(255, 255, 255, 0.04) inset;"
                borderRadius="10px"
                p={4}
              >
                <Flex direction="column">
                  <Text fontWeight="medium" fontSize="14px" lineHeight="16px" color="text.secondary" mb="3px">
                    {hasAlreadyStaked || hasActiveWithdrawRequest ? t('yourStake') : t('availableStake')}
                  </Text>
                  <Flex justifyContent="space-between" alignItems="center">
                    <HStack>
                      <Image boxSize={5} src="/assets/deepr-icon.png" />
                      <Text fontSize="24px" lineHeight="32px" fontWeight="700" color={valuesColor}>
                        {stakeInt}
                        <Text
                          as="span"
                          fontSize="24px"
                          lineHeight="32px"
                          fontWeight="700"
                          color="text.tertiary"
                          display="inline"
                        >
                          {'.'}
                          {stakeDecimals}
                        </Text>
                      </Text>
                    </HStack>
                    {hasAlreadyStaked && !hasActiveWithdrawRequest && (
                      <SmallButton
                        onClick={handleManageClick}
                        py="4px"
                        px="12px"
                        color="rgba(255, 255, 255, 0.72)"
                        fontSize="12px"
                        textTransform="none"
                        isDisabled={isProtocolDisabled}
                      >
                        {t('manage')}
                      </SmallButton>
                    )}
                  </Flex>

                  {distributionTokenPrice && (
                    <Text fontSize="14px" lineHeight="16px" color="text.tertiary" fontWeight="500" mt="3px">
                      {formatPriceNumberWithSymbol(distributionTokenPrice * stakeValueToDisplay)}
                    </Text>
                  )}
                </Flex>
              </Card>
              {!hasActiveWithdrawRequest && (
                <Card
                  justifyContent="center"
                  w={{ base: '100%' }}
                  h="102px"
                  alignItems={{ base: 'flex-start' }}
                  background={theme.colors.background.box}
                  dropShadow="box-shadow: 0px 1px 0px 0px rgba(255, 255, 255, 0.04) inset;"
                  borderRadius="10px"
                  p={4}
                >
                  <Flex direction="column">
                    <HStack spacing={0.5}>
                      <Text fontWeight="medium" fontSize="14px" lineHeight="16px" color="text.secondary" mb="3px">
                        {hasAlreadyStaked || hasActiveWithdrawRequest ? t('rewards') : t('potentialDailyRewards')}
                      </Text>
                      {!hasAlreadyStaked && !hasActiveWithdrawRequest && (
                        <HelpTooltip
                          label={t('potentialDailyRewardsTooltip')}
                          mt={1}
                          ml={1.5}
                          w="320px"
                          placement="bottom"
                        />
                      )}
                    </HStack>
                    <HStack>
                      <Image boxSize={5} src="/assets/deepr-icon.png" />
                      <Text fontSize="24px" lineHeight="32px" fontWeight="700" color={valuesColor}>
                        {rewardsInt}
                        <Text
                          as="span"
                          fontSize="24px"
                          lineHeight="32px"
                          fontWeight="700"
                          color="text.tertiary"
                          display="inline"
                        >
                          {'.'}
                          {rewardsDecimals}
                        </Text>
                      </Text>
                    </HStack>
                    {distributionTokenPrice && (
                      <Text fontSize="14px" lineHeight="16px" color="text.tertiary" fontWeight="500" mt="3px">
                        {hasActiveWithdrawRequest
                          ? formatPriceNumberWithSymbol(0)
                          : formatPriceNumberWithSymbol(
                              distributionTokenPrice *
                                (hasAlreadyStaked ? pendingRewards : marketStakingInfo.potentialDailyRewards)
                            )}
                      </Text>
                    )}
                  </Flex>
                </Card>
              )}
              <CustomButton
                mt={3}
                w="full"
                size={ButtonSize.LARGE}
                onClick={handleMainButtonClick}
                isLoading={isLoadingHarvest || isLoadingWithdraw}
                isDisabled={(hasActiveWithdrawRequest && !canWithdrawFunds) || isProtocolDisabled}
              >
                {getCTAButtonText()}
              </CustomButton>
              {hasActiveWithdrawRequest && !canWithdrawFunds && (
                <Text
                  mt={2}
                  fontWeight="medium"
                  fontSize="14px"
                  lineHeight="20px"
                  textAlign="center"
                  color="text.tertiary"
                >
                  {timeRemainingMessage}
                </Text>
              )}
            </Flex>
          </Flex>
        </Card>

        <StakeModal deeprStaking={deeprStaking} isOpen={isStakingModalOpen} onClose={onCloseStakingModal} />
      </Container>
    </>
  )
}

export default Staking
